import { useCallback, useState } from "react";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { Funnel } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { TextField, InputAdornment } from "@material-ui/core";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  Button,
  ButtonTypeEnum,
  Dialog,
  DialogContent,
  DialogTrigger,
  Icon,
  Loading,
  useLocationAutocomplete,
  type LocationAutocompleteProps,
} from "@tenant/ui";
import clsx from "clsx";
import { ApacUiComponent } from "../../context";
import { useApacUiStyles } from "../../hooks";
import defaultStyles from "./styles.module.scss";
import type { Places, Suggestion } from "./types";

export interface LocationAutoCompleteWithSuggestionProps<T extends Places>
  extends LocationAutocompleteProps<Suggestion<T>> {
  value?: Suggestion<T>;
  funnel?: Funnel;
  children?: React.ReactNode;
  title?: string;
  defaultOpen?: boolean;
  noDialog?: boolean;
  onClose?: () => void;
  renderSuggestions?: () => React.ReactNode;
  getOptionLabel?: (value: Suggestion<T>) => string;
  inputRef?: React.Ref<HTMLInputElement>;
}

export const LocationAutocompleteTrigger = <T extends Places>({
  label,
  value,
}: Pick<LocationAutoCompleteWithSuggestionProps<T>, "label" | "value">) => {
  const styles = useApacUiStyles(
    ApacUiComponent.LocationAutoCompleteWithSuggestion,
    defaultStyles
  );
  const [, cn] = useModuleBEM(
    styles,
    ApacUiComponent.LocationAutoCompleteWithSuggestion
  );

  return (
    <div className={cn("location-button")}>
      <div className={cn("content")}>
        <div
          className={cn("location-button-label", {
            filled: value?.label != null,
          })}
        >
          {label}
        </div>
        <div
          className={cn("location-button-value", {
            filled: value?.label != null,
          })}
        >
          {value?.displayLabel ?? value?.label}
        </div>
      </div>
      <div className={cn("icon")}>
        <Icon iconName={IconNameEnum.search} />
      </div>
    </div>
  );
};

export const LocationAutoCompleteWithSuggestion = <T extends Places>(
  props: LocationAutoCompleteWithSuggestionProps<T>
) => {
  const styles = useApacUiStyles(
    ApacUiComponent.LocationAutoCompleteWithSuggestion,
    defaultStyles
  );
  const [block] = useModuleBEM(
    styles,
    ApacUiComponent.LocationAutoCompleteWithSuggestion
  );

  const {
    label,
    value,
    className,
    disabled,
    children,
    title,
    defaultOpen = false,
    noDialog = false,
    onClose,
  } = props;
  const [isDialogOpen, setDialogOpen] = useState<boolean>(defaultOpen);
  const handleClose = useCallback(() => {
    setDialogOpen(false);
    onClose?.();
  }, [onClose]);

  if (noDialog) {
    return (
      <LocationAutocompleteWithSuggestionContent
        {...props}
        onClose={handleClose}
      />
    );
  }

  const defaultTrigger = (
    <LocationAutocompleteTrigger value={value} label={label} />
  );

  return (
    <Dialog modal={false} open={isDialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild={children == null} disabled={disabled}>
        {children != null ? children : defaultTrigger}
      </DialogTrigger>
      <DialogContent
        className={clsx(block, className)}
        fullScreen={true}
        title={title}
      >
        <LocationAutocompleteWithSuggestionContent
          {...props}
          onClose={handleClose}
        />
      </DialogContent>
    </Dialog>
  );
};

const LocationAutocompleteWithSuggestionContent = <T extends Places>(
  props: LocationAutoCompleteWithSuggestionProps<T> & {
    onClose: () => void;
  }
) => {
  const styles = useApacUiStyles(
    ApacUiComponent.LocationAutoCompleteWithSuggestion,
    defaultStyles
  );
  const [, cn] = useModuleBEM(
    styles,
    ApacUiComponent.LocationAutoCompleteWithSuggestion
  );
  const { t } = useI18nContext();

  const {
    id,
    label,
    value,
    onChange,
    placeholder,
    getOptionLabel,
    getOptionIcon,
    fetch,
    onClose,
    renderSuggestions,
    inputRef,
  } = props;

  const onLocationChange = useCallback(
    (location: Suggestion<T> | null) => {
      onChange(location);
      onClose();
    },
    [onClose, onChange]
  );

  const {
    context: { open, loading, search, options },
    handlers: { handleOnClose, handleOnInputChange, handleOnOpen, clearInput },
  } = useLocationAutocomplete({
    id,
    label,
    value,
    fetch,
    getOptionLabel,
    onChange: onLocationChange,
  });

  const showResult = search !== "";

  const input = (
    <TextField
      defaultValue={value ? getOptionLabel?.(value) : undefined}
      value={open ? search : undefined}
      onChange={(e) => handleOnInputChange(e, e.target.value)}
      inputRef={inputRef}
      onFocus={() => {
        handleOnOpen();
      }}
      onBlur={() => {
        handleOnClose();
      }}
      aria-label={label}
      role="combobox"
      aria-expanded={open}
      aria-controls="results-list"
      aria-autocomplete="list"
      placeholder={placeholder ?? label}
      variant="outlined"
      className={cn("input")}
      hiddenLabel={true}
      InputProps={{
        type: "search",
        endAdornment: (
          <InputAdornment
            position="end"
            className={cn("end-icons", { filled: value?.label != null })}
          >
            {loading ? (
              <Loading className={cn("loading-icon")} />
            ) : search === "" ? (
              <span>
                <Icon iconName={IconNameEnum.search} />
              </span>
            ) : (
              <Button
                buttonType={ButtonTypeEnum.Wrap}
                onMouseDown={(e) => {
                  e.preventDefault();
                  clearInput();
                }}
                aria-label={t("close.button")}
              >
                <Icon iconName={IconNameEnum.close} />
              </Button>
            )}
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <div className={cn("dialog-content")}>
      {input}
      <div className={cn("dialog-content-scrollable")}>
        {showResult && (
          <section className={cn("section")}>
            <VisuallyHidden role="status" aria-live="polite">
              {t("numResult", { count: options.length })}
            </VisuallyHidden>
            <h3>{t("locationSearch.resultTitle")}</h3>
            <ul
              className={cn("result-container")}
              id="results-list"
              role="listbox"
            >
              {options.map((suggestion) => (
                <Button
                  buttonType={ButtonTypeEnum.Wrap}
                  key={suggestion.label}
                  type="button"
                  role="option"
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                  onClick={() => {
                    onLocationChange(suggestion);
                  }}
                >
                  <div className={cn("result-item")}>
                    <div className={cn("result-icon")}>
                      {getOptionIcon?.(suggestion)}
                    </div>
                    {suggestion.displayLabel ?? suggestion.label}
                  </div>
                </Button>
              ))}
            </ul>
          </section>
        )}
        {!showResult && renderSuggestions?.()}
      </div>
    </div>
  );
};
